import type { PointerEvent as ReactPointerEvent } from 'react';
import {
  KeyboardCode,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type KeyboardCodes,
  type PointerSensorOptions,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

// Elements that must keep their own pointer behaviour instead of starting a
// drag, matching the previous drag-and-drop library's interactive-element
// blocking.
const INTERACTIVE_TAG_NAMES = [
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'optgroup',
  'video',
  'audio',
];

/**
 * True when `target` is, or sits inside, an interactive element that is a
 * descendant of the drag handle `handle`. The handle itself is not checked so
 * a `<button>` can still act as an explicit drag handle.
 */
export const isInteractiveDescendant = (
  target: EventTarget | null,
  handle: Element | null,
): boolean => {
  let current = target instanceof Element ? target : null;

  while (current && current !== handle) {
    if (INTERACTIVE_TAG_NAMES.includes(current.tagName.toLowerCase())) {
      return true;
    }
    const editable = current.getAttribute('contenteditable');
    if (editable === '' || editable === 'true') {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};

/**
 * Pointer sensor that ignores presses starting on interactive controls inside
 * the drag handle (buttons, inputs, ...), so those controls keep working.
 */
class HandlePointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (
        event: ReactPointerEvent,
        { onActivation }: PointerSensorOptions,
      ) => {
        const { nativeEvent } = event;
        if (!nativeEvent.isPrimary || nativeEvent.button !== 0) {
          return false;
        }
        if (isInteractiveDescendant(nativeEvent.target, event.currentTarget)) {
          return false;
        }
        onActivation?.({ event: nativeEvent });
        return true;
      },
    },
  ];
}

// Space lifts and drops, Escape cancels (previous library's keyboard model).
// Enter is left alone so it keeps activating the focused control.
const SORTABLE_KEYBOARD_CODES: KeyboardCodes = {
  start: [KeyboardCode.Space],
  cancel: [KeyboardCode.Esc],
  end: [KeyboardCode.Space],
};

// Small distance so clicks on the handle (or its children) still register as
// clicks rather than drags.
const SORTABLE_ACTIVATION_DISTANCE = 5;

export const useSortableSensors = () =>
  useSensors(
    useSensor(HandlePointerSensor, {
      activationConstraint: { distance: SORTABLE_ACTIVATION_DISTANCE },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: SORTABLE_KEYBOARD_CODES,
    }),
  );

export interface SortableMove {
  from: number;
  to: number;
}

/**
 * Converts a dnd-kit drag end into the source/destination indices that the
 * previous drag-and-drop library reported. Returns null for drops outside a
 * list, drops into another sortable container, and drops back in place.
 */
export const getSortableMove = ({
  active,
  over,
}: Pick<DragEndEvent, 'active' | 'over'>): SortableMove | null => {
  const source = active.data.current?.sortable;
  const destination = over?.data.current?.sortable;

  if (!source || !destination) {
    return null;
  }
  if (source.containerId !== destination.containerId) {
    return null;
  }
  if (source.index === destination.index) {
    return null;
  }

  return { from: source.index, to: destination.index };
};
